<template>
  <Modal
    class-name="accounting class-confirmation-modal"
    :title="modalTitle"
    :width="600"
    @hide-modal="emit('hide-modal')"
  >
    <div v-if="departmentChangePreview.invoice.changes_present" class="mb-2">
      Do you wish to update the accounting classes of this invoice and its line
      items according to the defaults of the new department?
    </div>
    <div>
      <div class="my-2">
        <div
          v-if="!departmentChangePreview.invoice.changes_present"
          data-cy="noUsages"
        >
          This department change would not result in any change in accounting
          classes for this invoice.
        </div>
        <div v-else>
          <div>The following accounting class changes will result:</div>
          <div class="summary">
            <div
              v-if="store.getters.trackClassesByInvoice"
              class="invoice-detail"
            >
              Invoice:
              <div class="ml-2">
                {{
                  accountingClassChange(
                    departmentChangePreview.invoice.current_accounting_class,
                    departmentChangePreview.invoice.new_accounting_class
                  )
                }}
              </div>
            </div>
            <div
              v-if="store.getters.trackClassesByLineItem"
              class="line-item-detail"
            >
              Line Items:
              <div
                v-for="lineItemChanges in departmentChangePreview.invoice
                  .line_items"
                :key="lineItemChanges.id"
                class="line-item-change ml-2"
              >
                <div>{{ lineItemChanges.unit_name }}:</div>
                <div class="ml-2">
                  {{
                    accountingClassChange(
                      lineItemChanges.current_accounting_class,
                      lineItemChanges.new_accounting_class
                    )
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="modal-footer d-flex flex-row">
        <SeraButton
          class="mr-2"
          text="Cancel"
          type="secondary"
          @click="handleCancel"
        />
        <div
          v-if="departmentChangePreview.invoice.changes_present"
          class="ml-auto"
        >
          <SeraButton
            data-cy="keepAsIsButton"
            type="secondary"
            text="Keep As-Is"
            @click="handleConfirm(false)"
          />
          <SeraButton
            data-cy="updateButton"
            type="primary"
            text="Update"
            class="ml-2"
            @click="handleConfirm(true)"
          />
        </div>
        <div v-else class="ml-auto">
          <SeraButton
            data-cy="continueButton"
            type="primary"
            text="Continue"
            class="ml-2"
            @click="handleConfirm(false)"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import { AccountingClass, DepartmentChangePreview } from "@/typings/accounting";

const emit = defineEmits(["hide-modal", "confirmed"]);

const props = defineProps({
  departmentChangePreview: {
    type: Object as () => DepartmentChangePreview,
    required: true,
    default: null
  }
});

const store = useStore();

const modalTitle = computed(() => {
  return props.departmentChangePreview.invoice.changes_present
    ? "Update Accounting Classes?"
    : "Accounting Classes";
});

function handleCancel() {
  emit("hide-modal");
}

async function handleConfirm(update: boolean) {
  try {
    emit("hide-modal");
    emit("confirmed", update);
  } catch (e) {
    console.error(e);
  }
}

function accountingClassChange(
  currentAccountingClass?: AccountingClass,
  newAccountingClass?: AccountingClass
) {
  const currentLabel = accountingClassLabel(currentAccountingClass);
  const newLabel = accountingClassLabel(newAccountingClass);

  return `${currentLabel} => ${newLabel}`;
}

function accountingClassLabel(accountingClass?: AccountingClass) {
  return accountingClass?.name ?? "(none)";
}
</script>

<style lang="scss">
.summary {
  background: var(--light-gray);
  border: 1px solid var(--gray);
  border-radius: 5px;
  padding: 10px;
}

.modal-footer {
  justify-content: flex-start;
}
</style>
